import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSurveys } from '../hooks/useSurveys';
import { SurveyDefinition } from '../types/survey.types';
import { useToast } from '../components/common/Toast/ToastContext';
import { functions } from '../utils/firebase';
import { httpsCallable } from 'firebase/functions';
import ResponseList from '../components/analytics/ResponseList';
import ChartInsights from '../components/analytics/ChartInsights';
import PDFExportModal from '../components/analytics/PDFExportModal';
import { AnalyticsPDFExporter, PDFExportOptions, AnalyticsData } from '../utils/pdfExport';
import { useResponses } from '../hooks/useResponses';
import { formatShortDate, daysBetween } from '../utils/dateFormatting';
import { getSurveyState, stateLabel } from '../utils/surveyState';

interface AnalyticsProps {}

const Analytics: React.FC<AnalyticsProps> = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const navigate = useNavigate();
  const { getSurvey, loading: surveyLoading } = useSurveys();
  const toast = useToast();

  const [survey, setSurvey] = useState<SurveyDefinition | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'responses' | 'charts'>('overview');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showPDFExportModal, setShowPDFExportModal] = useState(false);
  const { getResponses } = useResponses();

  useEffect(() => {
    loadSurvey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  const loadSurvey = async () => {
    if (!surveyId) {
      navigate('/admin');
      return;
    }

    try {
      setLoading(true);
      const surveyData = await getSurvey(surveyId);
      setSurvey(surveyData);
    } catch (error) {
      console.error('Error loading survey:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || surveyLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 mx-auto" style={{ borderBottom: '2px solid var(--color-accent)', borderTop: '2px solid transparent', borderLeft: '2px solid transparent', borderRight: '2px solid transparent' }}></div>
          <p className="mt-4" style={{ color: 'var(--color-muted)' }}>Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg)' }}>
        <div className="text-center">
          <h2 style={{
            fontFamily: 'var(--font-heading)',
            fontSize: '1.5rem',
            fontWeight: 600,
            color: 'var(--color-primary)',
            marginBottom: '1rem'
          }}>
            Survey Not Found
          </h2>
          <button
            onClick={() => navigate('/admin')}
            className="btn btn-primary"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const totalResponses = survey.analytics?.totalResponses || 0;
  const avgCompletionTime = survey.analytics?.averageCompletionTime || 0;
  const lastResponseAt = survey.analytics?.lastResponseAt;

  const formatLastResponse = (timestamp: any): string => {
    if (!timestamp) return 'No responses yet';

    try {
      let date: Date;

      // Handle Firestore Timestamp object
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      }
      // Handle Firestore Timestamp serialized object (has _seconds property)
      else if (timestamp._seconds !== undefined) {
        date = new Date(timestamp._seconds * 1000);
      }
      // Handle ISO string or timestamp number
      else {
        date = new Date(timestamp);
      }

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'No responses yet';
      }

      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch (error) {
      console.error('[Analytics] Error formatting lastResponseAt:', error, timestamp);
      return 'No responses yet';
    }
  };

  const handleExport = async (format: 'csv' | 'json') => {
    if (!surveyId) return;

    try {
      setExporting(true);
      toast.info('Generating export...');

      const generateExport = httpsCallable(functions, 'generateExport');
      const result = await generateExport({
        surveyId,
        format,
        includeMetadata: true,
      });

      const data = (result.data as any).data;

      // Create download link
      const blob = new Blob([data], { type: format === 'csv' ? 'text/csv' : 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${survey.title.replace(/[^a-z0-9]/gi, '_')}_responses.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Export downloaded successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handlePDFExport = async (options: PDFExportOptions) => {
    if (!surveyId) return;

    try {
      setExporting(true);
      toast.info('Generating PDF report...');

      // Get all responses
      const responses = await getResponses(surveyId);

      // Transform survey data to AnalyticsData format
      const questionAnalytics: any[] = [];

      survey.sections.forEach(section => {
        section.questions.forEach(question => {
          const questionResponses = responses
            .map(r => r.responses[question.id])
            .filter(r => r !== undefined && r !== null);

          if (questionResponses.length === 0) {
            return; // Skip questions with no responses
          }

          const analytics: any = {
            questionId: question.id,
            questionTitle: question.title,
            questionType: question.type,
            responseCount: questionResponses.length,
            totalSurveyRespondents: responses.length,
          };

          // Calculate distribution for choice-based questions
          if (['radio', 'checkbox', 'dropdown', 'yesno'].includes(question.type)) {
            const distribution: Record<string, { count: number; percentage: number; percentageOfTotal: number }> = {};

            questionResponses.forEach(response => {
              const value = response.value;

              if (Array.isArray(value)) {
                value.forEach(v => {
                  const key = String(v);
                  if (!distribution[key]) {
                    distribution[key] = { count: 0, percentage: 0, percentageOfTotal: 0 };
                  }
                  distribution[key].count++;
                });
              } else {
                const key = String(value);
                if (!distribution[key]) {
                  distribution[key] = { count: 0, percentage: 0, percentageOfTotal: 0 };
                }
                distribution[key].count++;
              }
            });

            // Calculate percentages
            Object.keys(distribution).forEach(key => {
              distribution[key].percentage = (distribution[key].count / questionResponses.length) * 100;
              distribution[key].percentageOfTotal = (distribution[key].count / responses.length) * 100;
            });

            analytics.distribution = distribution;
          }

          // Calculate numeric stats for number/rating/scale questions
          if (['number', 'rating', 'scale'].includes(question.type)) {
            const numericValues = questionResponses
              .map(r => typeof r.value === 'number' ? r.value : parseFloat(String(r.value)))
              .filter(v => !isNaN(v));

            if (numericValues.length > 0) {
              const sorted = [...numericValues].sort((a, b) => a - b);
              const sum = numericValues.reduce((a, b) => a + b, 0);

              analytics.numericStats = {
                min: sorted[0],
                max: sorted[sorted.length - 1],
                average: sum / numericValues.length,
                median: sorted.length % 2 === 0
                  ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
                  : sorted[Math.floor(sorted.length / 2)]
              };

              // Also create distribution for PDF
              const distribution: Record<string, { count: number; percentage: number; percentageOfTotal: number }> = {};
              numericValues.forEach(val => {
                const key = String(val);
                if (!distribution[key]) {
                  distribution[key] = { count: 0, percentage: 0, percentageOfTotal: 0 };
                }
                distribution[key].count++;
              });

              Object.keys(distribution).forEach(key => {
                distribution[key].percentage = (distribution[key].count / questionResponses.length) * 100;
                distribution[key].percentageOfTotal = (distribution[key].count / responses.length) * 100;
              });

              analytics.distribution = distribution;
            }
          }

          // Collect text responses
          if (['text', 'textarea', 'email'].includes(question.type)) {
            const textResponses = questionResponses
              .map(r => String(r.value))
              .filter(v => v && v.trim() !== '');

            analytics.textResponses = textResponses;
          }

          questionAnalytics.push(analytics);
        });
      });

      // Build AnalyticsData structure
      const analyticsData: AnalyticsData = {
        totalResponses: responses.length,
        completionRate: survey.analytics?.completionRate || 100,
        avgCompletionTime: survey.analytics?.averageCompletionTime || 0,
        sections: survey.sections.map(section => ({
          sectionId: section.id,
          sectionTitle: section.title,
          questions: questionAnalytics.filter(qa =>
            section.questions.some(q => q.id === qa.questionId)
          )
        }))
      };

      // Generate PDF
      const exporter = new AnalyticsPDFExporter();
      const pdfBlob = await exporter.generateReport(analyticsData, options);

      // Download
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${survey.title.replace(/[^a-z0-9]/gi, '_')}_Analytics_Report.pdf`;
      link.click();
      URL.revokeObjectURL(url);

      toast.success('PDF report generated successfully');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to generate PDF report');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-bg)' }}>
      {/* Header - Responsive */}
      <div className="bg-white" style={{ borderBottom: '3px solid var(--color-accent)' }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <button
                onClick={() => navigate('/admin')}
                className="mb-2 flex items-center"
                style={{
                  color: 'var(--color-accent)',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  fontWeight: 500
                }}
              >
                ← Back to Dashboard
              </button>
              <h1 className="truncate" style={{
                fontFamily: 'var(--font-heading)',
                fontSize: 'clamp(1.25rem, 4vw, 1.875rem)',
                fontWeight: 600,
                color: 'var(--color-primary)',
                marginBottom: '0.25rem'
              }}>
                {survey.title}
              </h1>
              <p style={{ color: 'var(--color-muted)', fontSize: '0.875rem' }}>
                Survey Analytics & Responses
              </p>
            </div>
            <div className="flex flex-wrap gap-2 sm:space-x-3 sm:gap-0">
              <div className="relative">
                <button
                  onClick={(e) => {
                    const menu = e.currentTarget.nextElementSibling as HTMLElement;
                    menu.classList.toggle('hidden');
                  }}
                  disabled={exporting}
                  className="btn btn-secondary"
                >
                  {exporting ? 'Exporting...' : 'Export Data'}
                </button>
                <div className="hidden absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                  <button
                    onClick={() => handleExport('csv')}
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 rounded-t-lg"
                    style={{ fontFamily: 'var(--font-body)', fontSize: '0.875rem' }}
                  >
                    📊 Export as CSV
                  </button>
                  <button
                    onClick={() => handleExport('json')}
                    className="w-full text-left px-4 py-2 hover:bg-gray-50"
                    style={{ fontFamily: 'var(--font-body)', fontSize: '0.875rem' }}
                  >
                    📋 Export as JSON
                  </button>
                  <div className="border-t my-1" style={{ borderColor: 'var(--color-border)' }}></div>
                  <button
                    onClick={() => {
                      setShowPDFExportModal(true);
                      const menu = document.querySelector('.hidden.absolute') as HTMLElement;
                      menu?.classList.add('hidden');
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 rounded-b-lg"
                    style={{ fontFamily: 'var(--font-body)', fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-accent)' }}
                  >
                    📄 Export PDF Report
                  </button>
                </div>
              </div>
              <button
                onClick={() => navigate(`/survey/${surveyId}`)}
                className="btn btn-primary"
              >
                View Survey
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Lifecycle Meta Strip — surfaces launchedAt / closedAt for analytics interpretation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        {(() => {
          const state = getSurveyState(survey);
          const launched = formatShortDate(survey.launchedAt, '—');
          const closed = formatShortDate(survey.closedAt, '—');
          const stateColor = ({
            draft:  '#6b7280',
            active: '#8f6f2a',
            paused: '#264372',
            closed: '#a4503e',
          } as const)[state];
          const stateBg = ({
            draft:  'rgba(107, 114, 128, 0.1)',
            active: 'rgba(199, 161, 74, 0.15)',
            paused: 'rgba(38, 67, 114, 0.1)',
            closed: 'rgba(199, 88, 74, 0.1)',
          } as const)[state];

          let detail: React.ReactNode;
          if (state === 'draft') {
            detail = <span style={{ color: 'var(--color-muted)' }}>not yet launched</span>;
          } else if (state === 'active') {
            const days = survey.launchedAt ? daysBetween(survey.launchedAt, new Date()) : 0;
            detail = (
              <>
                <strong style={{ color: 'var(--color-primary)' }}>Launched</strong> {launched}
                <span style={{ color: 'var(--color-muted)' }}> · {days} day{days === 1 ? '' : 's'} running</span>
              </>
            );
          } else if (state === 'paused') {
            detail = (
              <>
                <strong style={{ color: 'var(--color-primary)' }}>Launched</strong> {launched}
              </>
            );
          } else {
            const window = survey.launchedAt && survey.closedAt
              ? daysBetween(survey.launchedAt, survey.closedAt)
              : 0;
            detail = (
              <>
                <strong style={{ color: 'var(--color-primary)' }}>Launched</strong> {launched}
                <span style={{ color: 'var(--color-muted)' }}> → </span>
                <strong style={{ color: 'var(--color-primary)' }}>Closed</strong> {closed}
                {window > 0 && (
                  <span style={{ color: 'var(--color-muted)' }}> ({window} day{window === 1 ? '' : 's'})</span>
                )}
              </>
            );
          }

          return (
            <div
              className="flex flex-wrap items-center gap-3 px-4 py-3 rounded-lg border"
              style={{
                backgroundColor: 'white',
                borderColor: 'var(--color-border)',
                fontSize: '0.875rem',
                fontFamily: 'var(--font-body)',
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  padding: '0.25rem 0.625rem',
                  borderRadius: '999px',
                  backgroundColor: stateBg,
                  color: stateColor,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {stateLabel(state)}
              </span>
              <span style={{ color: 'var(--color-primary)' }}>{detail}</span>
            </div>
          );
        })()}
      </div>

      {/* Stats Cards - Crisci Branded */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Total Responses */}
          <div className="bg-white rounded-lg border p-4" style={{ borderColor: 'var(--color-border)' }}>
            <div className="text-center">
              <p style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '2.25rem',
                fontWeight: 600,
                color: 'var(--color-accent)',
                marginBottom: '0.5rem'
              }}>
                {totalResponses.toLocaleString()}
              </p>
              <p style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'var(--color-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Total Responses
              </p>
            </div>
          </div>

          {/* Avg Completion Time */}
          <div className="bg-white rounded-lg border p-4" style={{ borderColor: 'var(--color-border)' }}>
            <div className="text-center">
              <p style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '2.25rem',
                fontWeight: 600,
                color: 'var(--color-primary)',
                marginBottom: '0.5rem'
              }}>
                {Math.round(avgCompletionTime / 60)}m
              </p>
              <p style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'var(--color-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Avg Completion Time
              </p>
            </div>
          </div>

          {/* Last Response */}
          <div className="bg-white rounded-lg border p-4" style={{ borderColor: 'var(--color-border)' }}>
            <div className="text-center">
              <p style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.9375rem',
                fontWeight: 600,
                color: 'var(--color-primary)',
                marginBottom: '0.5rem'
              }}>
                {formatLastResponse(lastResponseAt)}
              </p>
              <p style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'var(--color-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Last Response
              </p>
            </div>
          </div>
        </div>

        {/* Tabs - Crisci Branded */}
        <div className="bg-white rounded-lg border" style={{ borderColor: 'var(--color-border)' }}>
          <div style={{ borderBottom: '1px solid var(--color-border)' }}>
            <nav className="-mb-px flex space-x-6 px-4" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('overview')}
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  padding: '0.75rem 0.25rem',
                  borderBottom: activeTab === 'overview' ? '3px solid var(--color-accent)' : '3px solid transparent',
                  color: activeTab === 'overview' ? 'var(--color-accent)' : 'var(--color-muted)',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s'
                }}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('responses')}
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  padding: '0.75rem 0.25rem',
                  borderBottom: activeTab === 'responses' ? '3px solid var(--color-accent)' : '3px solid transparent',
                  color: activeTab === 'responses' ? 'var(--color-accent)' : 'var(--color-muted)',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s'
                }}
              >
                Responses ({totalResponses})
              </button>
              <button
                onClick={() => setActiveTab('charts')}
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  padding: '0.75rem 0.25rem',
                  borderBottom: activeTab === 'charts' ? '3px solid var(--color-accent)' : '3px solid transparent',
                  color: activeTab === 'charts' ? 'var(--color-accent)' : 'var(--color-muted)',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s'
                }}
              >
                Charts & Insights
              </button>
            </nav>
          </div>

          {/* Tab Content - Responsive Padding */}
          <div className="p-3 sm:p-4 md:p-6">
            {activeTab === 'overview' && (
              <div>
                <h3 style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  color: 'var(--color-primary)',
                  marginBottom: '1.5rem'
                }}>
                  Survey Overview
                </h3>

                {totalResponses === 0 ? (
                  <div className="text-center py-12">
                    <h4 style={{
                      fontFamily: 'var(--font-heading)',
                      fontSize: '1.25rem',
                      fontWeight: 600,
                      color: 'var(--color-primary)',
                      marginBottom: '0.5rem'
                    }}>
                      No responses yet
                    </h4>
                    <p style={{ color: 'var(--color-muted)', marginBottom: '1.5rem' }}>
                      Share your survey to start collecting responses
                    </p>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/survey/${surveyId}`);
                        toast.success('Survey link copied to clipboard!');
                      }}
                      className="btn btn-primary"
                    >
                      Copy Survey Link
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg border p-5" style={{ borderColor: 'var(--color-border)' }}>
                      <h4 style={{
                        fontFamily: 'var(--font-heading)',
                        fontSize: '1rem',
                        fontWeight: 600,
                        color: 'var(--color-primary)',
                        marginBottom: '1rem'
                      }}>
                        Survey Structure
                      </h4>
                      <div className="space-y-3" style={{ fontSize: '0.875rem' }}>
                        <div className="flex justify-between">
                          <span style={{ color: 'var(--color-muted)' }}>Sections:</span>
                          <span style={{
                            fontFamily: 'var(--font-heading)',
                            fontWeight: 600,
                            color: 'var(--color-primary)'
                          }}>
                            {survey.sections.length}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span style={{ color: 'var(--color-muted)' }}>Questions:</span>
                          <span style={{
                            fontFamily: 'var(--font-heading)',
                            fontWeight: 600,
                            color: 'var(--color-primary)'
                          }}>
                            {survey.sections.reduce((sum, section) => sum + section.questions.length, 0)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span style={{ color: 'var(--color-muted)' }}>Status:</span>
                          <span style={{
                            fontWeight: 600,
                            color: survey.settings?.isActive ? 'var(--color-success)' : 'var(--color-error)'
                          }}>
                            {survey.settings?.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-lg border p-5" style={{ borderColor: 'var(--color-border)' }}>
                      <h4 style={{
                        fontFamily: 'var(--font-heading)',
                        fontSize: '1rem',
                        fontWeight: 600,
                        color: 'var(--color-primary)',
                        marginBottom: '1rem'
                      }}>
                        Performance
                      </h4>
                      <div className="space-y-3" style={{ fontSize: '0.875rem' }}>
                        <div className="flex justify-between">
                          <span style={{ color: 'var(--color-muted)' }}>Response Rate:</span>
                          <span style={{
                            fontFamily: 'var(--font-heading)',
                            fontWeight: 600,
                            color: 'var(--color-accent)'
                          }}>
                            {totalResponses > 0 ? '100%' : '0%'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span style={{ color: 'var(--color-muted)' }}>Avg Time:</span>
                          <span style={{
                            fontFamily: 'var(--font-heading)',
                            fontWeight: 600,
                            color: 'var(--color-primary)'
                          }}>
                            {avgCompletionTime > 0
                              ? `${Math.round(avgCompletionTime / 60)}m ${Math.round(avgCompletionTime % 60)}s`
                              : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'responses' && surveyId && (
              <ResponseList surveyId={surveyId} survey={survey} />
            )}

            {activeTab === 'charts' && surveyId && (
              <ChartInsights
                surveyId={surveyId}
                survey={survey}
                onExportPDF={() => setShowPDFExportModal(true)}
              />
            )}
          </div>
        </div>
      </div>

      {/* PDF Export Modal */}
      {showPDFExportModal && (
        <PDFExportModal
          isOpen={showPDFExportModal}
          onClose={() => setShowPDFExportModal(false)}
          onExport={handlePDFExport}
          survey={survey}
          totalResponses={totalResponses}
        />
      )}
    </div>
  );
};

export default Analytics;
